"use client";

import { X } from "@phosphor-icons/react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

import type { ArchivePhoto, ArchiveCollection } from "@/lib/yearGallery";

type Props = {
  collections: ArchiveCollection[];
  year: string;
  copy: { photographs: string; openPhoto: string; closePhoto: string; expandedPhoto: string; gathering: string; day: string; sports: string };
};

export function YearPhotoGallery({ collections, year, copy }: Props) {
  const [active, setActive] = useState<ArchivePhoto | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActive(null);
      if (event.key === "Tab") {
        event.preventDefault();
        closeButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      openerRef.current?.focus();
    };
  }, [active]);

  return (
    <section className="bg-[#F5F1E8] px-4 py-20 text-[#171311] dark:bg-[#11100F] dark:text-[#F5F1E8] md:px-8 md:py-28">
      <div className="mx-auto max-w-7xl">
        {collections.map((collection, collectionIndex) => (
          <div key={collection.id} id={collection.id} className={collectionIndex ? "mt-28" : ""}>
            <div className="mb-8 flex items-end justify-between gap-6 border-b border-current/20 pb-5">
              <div>
                <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-brand-red dark:text-[#FF6969]">
                  {collection.dateLabel}
                </p>
                <h2 className="mt-2 font-serif text-4xl font-semibold md:text-6xl">{collection.id === "gathering" ? copy.gathering : collection.id === "sports" ? copy.sports : `${copy.day} ${collection.id.slice(-1)}`}</h2>
              </div>
              <p className="pb-2 text-sm font-bold tabular-nums">{collection.photos.length} {copy.photographs}</p>
            </div>
            <div className="columns-2 gap-3 md:columns-3 md:gap-4 lg:columns-4">
              {collection.photos.map((photo, index) => (
                <button
                  key={photo.src}
                  type="button"
                  onClick={(event) => { openerRef.current = event.currentTarget; setActive(photo); }}
                  aria-label={`${copy.openPhoto} ${year} ${index + 1}`}
                  className="group relative mb-3 block w-full break-inside-avoid overflow-hidden bg-zinc-300 text-left focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-red md:mb-4"
                >
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    width={photo.portrait ? 900 : 1400}
                    height={photo.portrait ? 1200 : 900}
                    sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
                    className="h-auto w-full transition-transform duration-700 ease-out group-hover:scale-[1.025]"
                  />
                  <span className="absolute bottom-3 right-3 grid size-8 place-items-center bg-[#F5F1E8] font-display text-[10px] font-black text-[#171311] opacity-0 transition-opacity group-hover:opacity-100">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {active && (
        <div role="dialog" aria-modal="true" aria-label={copy.expandedPhoto} className="fixed inset-0 z-[200] grid place-items-center bg-black/95 p-4 md:p-10">
          <button ref={closeButtonRef} type="button" onClick={() => setActive(null)} aria-label={copy.closePhoto} className="absolute right-5 top-5 grid size-12 place-items-center text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white">
            <X size={26} weight="bold" />
          </button>
          <Image src={active.src} alt={active.alt} width={1600} height={1200} className="max-h-[88vh] w-auto max-w-full object-contain" />
        </div>
      )}
    </section>
  );
}
